/**
 * Shared viewport state.
 *
 * The renderer (CanvasGL) and annotation layer (AnnotationLayer) both
 * need the same {zoom, pan, canvas size, image size} so that:
 *
 *   - the SVG overlay transforms with the image and clicks land on
 *     the correct image-space pixel;
 *   - tools can reset the view / do zoom-to-fit consistently;
 *   - crosshair sync across viewports is possible later.
 *
 * A single `createViewportStore()` per StudyViewer is created and
 * handed to both layers via props.
 */
import { createSignal, type Accessor } from "solid-js";

export interface Pan { x: number; y: number; }

export interface ViewportState {
  /** Canvas CSS pixels (container). */
  canvasW: () => number;
  canvasH: () => number;
  setCanvasSize: (w: number, h: number) => void;

  /** Image pixel dimensions (from the current frame). */
  imageW: () => number;
  imageH: () => number;
  setImageSize: (w: number, h: number) => void;

  /** Zoom multiplier on top of the fit-to-canvas base. */
  zoom: () => number;
  setZoom: (z: number) => void;

  /** Pan in CSS pixels, (0,0) is image centred in canvas. */
  pan: () => Pan;
  setPan: (p: Pan) => void;

  /** Display transforms. */
  invert: () => boolean;
  setInvert: (b: boolean) => void;
  flipH: () => boolean;
  setFlipH: (b: boolean) => void;
  flipV: () => boolean;
  setFlipV: (b: boolean) => void;

  /** Derived geometry. */
  fit: Accessor<number>;           // scale-to-fit the image into the canvas
  scale: Accessor<number>;         // fit * zoom
  imageOrigin: Accessor<Pan>;      // CSS pixel of image (0,0) in canvas space

  /** screen (px) → image (px). */
  screenToImage: (sx: number, sy: number) => Pan;
  /** image (px) → screen (px). */
  imageToScreen: (ix: number, iy: number) => Pan;

  /** Reset zoom + pan. */
  reset: () => void;
}

export function createViewportStore(): ViewportState {
  const [canvasW, setCW] = createSignal(1);
  const [canvasH, setCH] = createSignal(1);
  const [imageW,  setIW] = createSignal(1);
  const [imageH,  setIH] = createSignal(1);
  const [zoom,    setZoom] = createSignal(1);
  const [pan,     setPan]  = createSignal<Pan>({ x: 0, y: 0 });
  const [invert,  setInvert] = createSignal(false);
  const [flipH,   setFlipH]  = createSignal(false);
  const [flipV,   setFlipV]  = createSignal(false);

  const fit = () => {
    const w = canvasW(), h = canvasH();
    const iw = imageW(), ih = imageH();
    if (!w || !h || !iw || !ih) return 1;
    return Math.min(w / iw, h / ih);
  };
  const scale = () => fit() * zoom();
  const imageOrigin = (): Pan => {
    const s = scale();
    const ox = (canvasW() - imageW() * s) / 2 + pan().x;
    const oy = (canvasH() - imageH() * s) / 2 + pan().y;
    return { x: ox, y: oy };
  };

  const screenToImage = (sx: number, sy: number): Pan => {
    const s = scale();
    const o = imageOrigin();
    let ix = (sx - o.x) / s;
    let iy = (sy - o.y) / s;
    if (flipH()) ix = imageW() - ix;
    if (flipV()) iy = imageH() - iy;
    return { x: ix, y: iy };
  };
  const imageToScreen = (ix: number, iy: number): Pan => {
    const s = scale();
    const o = imageOrigin();
    let xx = ix;
    let yy = iy;
    if (flipH()) xx = imageW() - xx;
    if (flipV()) yy = imageH() - yy;
    return { x: o.x + xx * s, y: o.y + yy * s };
  };

  return {
    canvasW, canvasH,
    setCanvasSize: (w, h) => { setCW(w); setCH(h); },
    imageW, imageH,
    setImageSize:  (w, h) => { setIW(w); setIH(h); },
    zoom, setZoom,
    pan, setPan,
    invert, setInvert,
    flipH, setFlipH,
    flipV, setFlipV,
    fit, scale, imageOrigin,
    screenToImage, imageToScreen,
    reset: () => { setZoom(1); setPan({ x: 0, y: 0 }); },
  };
}
